import { Button, Card, EmptyState, Input, Spinner, Switch } from "@qn/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  Contact as ContactIcon,
  ListChecks,
  MessageSquare,
  PanelLeft,
  Phone,
  Plug,
  UserRound,
} from "lucide-react";
import { useState } from "react";
import { AssistantCall } from "../components/AssistantCall";
import { api } from "../lib/api";
import { useCall } from "../lib/call-store";
import { useFeature } from "../lib/features";
import { useT } from "../lib/i18n";
import { usePrefs } from "../lib/prefs";
import { useSettingsQuery } from "../lib/settings";
import { DEFAULT_VOICE, unlockAudio } from "../lib/voice";

type Tab = "errands" | "threads" | "contacts" | "calendar" | "channels";
interface Contact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  linked_user_id: string | null;
  preferred: string;
  calendar_share: "none" | "busy";
  channels: string[];
}
interface Thread {
  id: string;
  contact_name: string;
  channel: string;
  mission: string;
  status: string;
  summary: string;
  call_state?: string | null;
  updated_at: number;
}
interface Msg {
  id: string;
  direction: "out" | "in" | "note";
  sender: string;
  body: string;
  channel: string;
  status: string;
  created_at: number;
}
const when = (ms: number) =>
  new Date(ms).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
const STATUS_COLOR: Record<string, string> = {
  waiting: "text-[var(--fg-muted)]",
  replied: "text-[var(--accent-text)]",
  escalated: "text-[var(--warning,#d97706)]",
  done: "text-[var(--success)]",
  failed: "text-[var(--danger)]",
  closed: "text-[var(--fg-faint)]",
};

/** An in-app voice call: the assistant speaks first, then you talk with it hands-free (Egyptian dialect voice when available). */
interface ErrandRow {
  id: string;
  title: string;
  status: "working" | "waiting" | "needs_you" | "done" | "cancelled";
  summary: string;
  last_event: string | null;
  updated_at: number;
}
const ERRAND_ICON: Record<string, string> = {
  working: "⏳",
  waiting: "🕐",
  needs_you: "⚠️",
  done: "✅",
  cancelled: "🚫",
};
const ERRAND_COLOR: Record<string, string> = {
  working: "text-[var(--fg-muted)]",
  waiting: "text-[var(--accent-text)]",
  needs_you: "text-[var(--warning,#d97706)]",
  done: "text-[var(--success)]",
  cancelled: "text-[var(--fg-faint)]",
};

/** Tasks the assistant is working on for you: status at a glance, a timeline per task, and a way to stop one. */
function Errands() {
  const t = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const list = useQuery({
    queryKey: ["errands", all],
    queryFn: () =>
      api<{ errands: ErrandRow[] }>(`/assistant/errands?scope=${all ? "all" : "open"}`),
    refetchInterval: 15000,
  });
  const detail = useQuery({
    queryKey: ["errand", open],
    enabled: !!open,
    queryFn: () =>
      api<{
        errand: ErrandRow & { goal: string };
        events: Array<{ id: string; kind: string; text: string; created_at: number }>;
        threads: Array<{ id: string; status: string; channel: string; contact_name: string }>;
      }>(`/assistant/errands/${open}`),
    refetchInterval: 8000,
  });
  const cancel = useMutation({
    mutationFn: (id: string) =>
      api(`/assistant/errands/${id}/cancel`, { method: "POST", body: {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["errands"] });
      void qc.invalidateQueries({ queryKey: ["errand"] });
    },
  });
  const label = (s: string) => t(`errand.status.${s}` as "errand.status.working");
  if (open && detail.data) {
    const e = detail.data.errand;
    return (
      <div className="space-y-3">
        <Button variant="ghost" onClick={() => setOpen(null)}>
          ← {t("assistant.back")}
        </Button>
        <div className="space-y-1" dir="auto">
          <h3 className="font-display text-lg font-semibold">{e.title}</h3>
          <p className={`text-sm ${ERRAND_COLOR[e.status] ?? ""}`}>
            {ERRAND_ICON[e.status]} {label(e.status)}
            {e.summary ? ` — ${e.summary}` : ""}
          </p>
        </div>
        <ol
          className="space-y-2 border-s border-[var(--line)] ps-4"
          aria-label={t("errand.timeline")}
        >
          {detail.data.events.map((ev) => (
            <li key={ev.id} className="text-sm" dir="auto">
              <p>{ev.text}</p>
              <p className="text-[11px] text-[var(--fg-faint)]">
                {t(`errand.kind.${ev.kind}` as "errand.kind.created")} · {when(ev.created_at)}
              </p>
            </li>
          ))}
        </ol>
        <div className="flex flex-wrap gap-2">
          {detail.data.threads.map((th) => (
            <Button key={th.id} size="sm" variant="outline" onClick={() => setOpen(null)}>
              {th.contact_name} · {t(`assistant.status.${th.status}` as "assistant.status.waiting")}
            </Button>
          ))}
          {["working", "waiting", "needs_you"].includes(e.status) && (
            <Button
              size="sm"
              variant="outline"
              loading={cancel.isPending}
              onClick={() => cancel.mutate(e.id)}
            >
              {t("errand.cancel")}
            </Button>
          )}
        </div>
      </div>
    );
  }
  const rows = list.data?.errands ?? [];
  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--fg-muted)]">{t("errand.hint")}</p>
      <div className="flex items-center gap-2 text-xs text-[var(--fg-muted)]">
        <Switch checked={all} onCheckedChange={setAll} label={t("errand.showAll")} />
        <span>{t("errand.showAll")}</span>
      </div>
      {list.isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<ListChecks className="size-6" />}
          title={t("errand.empty")}
          hint={t("errand.emptyHint")}
        />
      ) : (
        <ul className="space-y-2">
          {rows.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => setOpen(e.id)}
                className="w-full rounded-xl border border-[var(--line)] p-3 text-start text-sm hover:bg-[var(--surface-3)]"
              >
                <div className="flex items-center justify-between gap-2" dir="auto">
                  <span className="font-medium">{e.title}</span>
                  <span className={`shrink-0 text-xs ${ERRAND_COLOR[e.status] ?? ""}`}>
                    {ERRAND_ICON[e.status]} {label(e.status)}
                  </span>
                </div>
                {(e.summary || e.last_event) && (
                  <p className="mt-1 line-clamp-2 text-[var(--fg-muted)]" dir="auto">
                    {e.summary || e.last_event}
                  </p>
                )}
                <p className="mt-1 text-[11px] text-[var(--fg-faint)]">{when(e.updated_at)}</p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Threads() {
  const t = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [call, setCall] = useState<string | null>(null);
  const [text, setText] = useState("");
  const list = useQuery({
    queryKey: ["threads"],
    queryFn: () => api<{ threads: Thread[] }>("/assistant/threads"),
    refetchInterval: 15000,
  });
  const inbox = useQuery({
    queryKey: ["inbox"],
    queryFn: () =>
      api<{
        threads: Array<{
          id: string;
          from_name: string;
          mission: string;
          status: string;
          voice: number;
        }>;
      }>("/assistant/inbox"),
    refetchInterval: 15000,
  });
  const detail = useQuery({
    queryKey: ["thread", open],
    enabled: !!open,
    queryFn: () =>
      api<{ messages: Msg[] }>(
        open?.startsWith("in:")
          ? `/assistant/inbox/${open.slice(3)}`
          : `/assistant/threads/${open}`,
      ),
    refetchInterval: 8000,
  });
  const isInbox = !!open?.startsWith("in:");
  const [verbatimReply, setVerbatimReply] = useState(false);
  const reply = useMutation({
    mutationFn: () =>
      api(
        isInbox ? `/assistant/inbox/${open?.slice(3)}/reply` : `/assistant/threads/${open}/reply`,
        {
          method: "POST",
          body: { message: text, ...(!isInbox && verbatimReply ? { verbatim: true } : {}) },
        },
      ),
    onSuccess: () => {
      setText("");
      void qc.invalidateQueries({ queryKey: ["thread", open] });
      void qc.invalidateQueries({ queryKey: ["threads"] });
    },
  });
  const close = useMutation({
    mutationFn: () => api(`/assistant/threads/${open}/close`, { method: "POST", body: {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["threads"] }),
  });
  if (open)
    return (
      <div className="space-y-3">
        <Button variant="ghost" onClick={() => setOpen(null)}>
          ← {t("assistant.back")}
        </Button>
        <ul className="space-y-2" aria-live="polite">
          {(detail.data?.messages ?? []).map((m) => (
            <li
              key={m.id}
              className={`max-w-[85%] rounded-xl border border-[var(--line)] p-2 text-sm ${m.sender === "contact" ? "" : "ms-auto bg-[var(--surface-3)]"} ${m.status === "failed" ? "border-[var(--danger)]" : ""}`}
              dir="auto"
            >
              <p>{m.body}</p>
              <p className="mt-1 text-[11px] text-[var(--fg-faint)]">
                {m.direction === "note"
                  ? t("assistant.note")
                  : t(`assistant.sender.${m.sender}` as "assistant.sender.contact")}{" "}
                · {m.channel} · {when(m.created_at)}
                {m.status === "failed" ? ` · ${t("assistant.failed")}` : ""}
              </p>
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <Input
            value={text}
            dir="auto"
            aria-label={t("assistant.write")}
            placeholder={t("assistant.write")}
            onChange={(e) => setText(e.target.value)}
          />
          <Button loading={reply.isPending} disabled={!text.trim()} onClick={() => reply.mutate()}>
            {t("assistant.send")}
          </Button>
          {!isInbox && (
            <Button variant="outline" onClick={() => close.mutate()}>
              {t("assistant.close")}
            </Button>
          )}
        </div>
        {!isInbox && (
          <div className="flex items-center gap-2 text-xs text-[var(--fg-muted)]">
            <Switch
              checked={verbatimReply}
              onCheckedChange={setVerbatimReply}
              label={t("assistant.verbatim")}
            />
            <span>{t("assistant.verbatim")}</span>
          </div>
        )}
        {reply.error && (
          <p role="alert" className="text-xs text-[var(--danger)]">
            {reply.error.message}
          </p>
        )}
      </div>
    );
  return (
    <div className="space-y-4">
      {call && <AssistantCall threadId={call} onClose={() => setCall(null)} />}
      {!!inbox.data?.threads.length && (
        <div>
          <h3 className="mb-2 text-sm font-semibold">{t("assistant.inbox")}</h3>
          <ul className="space-y-2">
            {inbox.data.threads.map((x) => (
              <li key={x.id}>
                <button
                  type="button"
                  className="w-full rounded-xl border border-[var(--line)] p-3 text-start text-sm hover:bg-[var(--surface-3)]"
                  onClick={() => setOpen(`in:${x.id}`)}
                  dir="auto"
                >
                  <span className="font-medium">{x.from_name}</span> — {x.mission}
                </button>
                {x.voice === 1 && (
                  <Button
                    className="mt-1"
                    variant="primary"
                    onClick={() => {
                      unlockAudio();
                      setCall(x.id);
                    }}
                  >
                    📞 {t("assistant.answer")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {list.isLoading ? (
        <Spinner />
      ) : !list.data?.threads.length ? (
        <EmptyState
          icon={<MessageSquare className="size-6" />}
          title={t("assistant.noThreads")}
          hint={t("assistant.noThreadsHint")}
        />
      ) : (
        <ul className="space-y-2">
          {list.data.threads.map((x) => (
            <li key={x.id}>
              <button
                type="button"
                className="w-full rounded-xl border border-[var(--line)] p-3 text-start text-sm hover:bg-[var(--surface-3)]"
                onClick={() => setOpen(x.id)}
                dir="auto"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{x.contact_name}</span>
                  <span className={`text-xs ${STATUS_COLOR[x.status] ?? ""}`}>
                    {t(`assistant.status.${x.status}` as "assistant.status.waiting")}
                  </span>
                </div>
                <p className="mt-1 text-[var(--fg-muted)]">{x.summary || x.mission}</p>
                {x.call_state && (
                  <p className="mt-1 text-[11px] text-[var(--fg-faint)]">
                    📞 {t(`assistant.callState.${x.call_state}` as "assistant.callState.ringing")}
                  </p>
                )}
                <p className="mt-1 text-[11px] text-[var(--fg-faint)]">
                  {x.channel} · {when(x.updated_at)}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Contacts() {
  const t = useT();
  const dial = useCall((s) => s.dial);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["contacts"],
    queryFn: () => api<{ contacts: Contact[] }>("/assistant/contacts"),
  });
  const [f, setF] = useState({ name: "", phone: "", email: "" });
  const [sendTo, setSendTo] = useState<string | null>(null);
  const [msg, setMsg] = useState("");
  const [channel, setChannel] = useState("");
  const [verbatim, setVerbatim] = useState(false);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["contacts"] });
  const add = useMutation({
    mutationFn: () =>
      api("/assistant/contacts", {
        method: "POST",
        body: { name: f.name, phone: f.phone || null, email: f.email || null },
      }),
    onSuccess: () => {
      setF({ name: "", phone: "", email: "" });
      refresh();
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/assistant/contacts/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
  });
  const share = useMutation({
    mutationFn: (c: Contact) =>
      api(`/assistant/contacts/${c.id}`, {
        method: "PUT",
        body: {
          name: c.name,
          email: c.email,
          phone: c.phone,
          preferred: c.preferred,
          calendarShare: c.calendar_share === "busy" ? "none" : "busy",
        },
      }),
    onSuccess: refresh,
  });
  const send = useMutation({
    mutationFn: () =>
      api("/assistant/send", {
        method: "POST",
        body: {
          contactId: sendTo,
          message: msg,
          ...(channel ? { channel } : {}),
          ...(verbatim ? { verbatim: true } : {}),
        },
      }),
    onSuccess: () => {
      setSendTo(null);
      setMsg("");
      void qc.invalidateQueries({ queryKey: ["threads"] });
    },
  });
  return (
    <div className="space-y-4">
      <Card className="space-y-2 p-3">
        <p className="text-sm font-medium">{t("assistant.addContact")}</p>
        <Input
          aria-label={t("assistant.name")}
          placeholder={t("assistant.name")}
          dir="auto"
          value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })}
        />
        <div className="grid gap-2 sm:grid-cols-2">
          <Input
            dir="ltr"
            aria-label={t("assistant.phone")}
            placeholder="+20 100 123 4567"
            value={f.phone}
            onChange={(e) => setF({ ...f, phone: e.target.value })}
          />
          <Input
            dir="ltr"
            aria-label="Email"
            placeholder="name@example.com"
            value={f.email}
            onChange={(e) => setF({ ...f, email: e.target.value })}
          />
        </div>
        <Button loading={add.isPending} disabled={!f.name.trim()} onClick={() => add.mutate()}>
          {t("action.save")}
        </Button>
        {add.error && (
          <p role="alert" className="text-xs text-[var(--danger)]">
            {add.error.message}
          </p>
        )}
      </Card>
      {!q.data?.contacts.length ? (
        <EmptyState
          icon={<UserRound className="size-6" />}
          title={t("assistant.noContacts")}
          hint={t("assistant.noContactsHint")}
        />
      ) : (
        <ul className="space-y-2">
          {q.data.contacts.map((c) => (
            <li key={c.id} className="rounded-xl border border-[var(--line)] p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div dir="auto">
                  <p className="font-medium">{c.name}</p>
                  <p className="text-xs text-[var(--fg-muted)]" dir="ltr">
                    {[c.phone, c.email].filter(Boolean).join(" · ")}
                  </p>
                  <p className="text-[11px] text-[var(--fg-faint)]">
                    {c.channels.length ? c.channels.join(" · ") : t("assistant.noChannel")}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-xs text-[var(--fg-muted)]">
                    <Switch
                      checked={c.calendar_share === "busy"}
                      onCheckedChange={() => share.mutate(c)}
                      label={t("assistant.shareFree")}
                    />
                    {t("assistant.shareFree")}
                  </div>
                  {c.linked_user_id && (
                    <Button
                      variant="primary"
                      size="icon"
                      aria-label={`${t("call.callBtn")} ${c.name}`}
                      onClick={() => {
                        unlockAudio();
                        void dial(c.id, c.name);
                      }}
                    >
                      <Phone className="size-4" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    disabled={!c.channels.length}
                    onClick={() => {
                      setSendTo(c.id);
                      setChannel("");
                    }}
                  >
                    {t("assistant.message")}
                  </Button>
                  <Button variant="ghost" onClick={() => del.mutate(c.id)}>
                    {t("action.delete")}
                  </Button>
                </div>
              </div>
              {sendTo === c.id && (
                <div className="mt-3 space-y-2">
                  <Input
                    dir="auto"
                    aria-label={t("assistant.write")}
                    placeholder={t("assistant.write")}
                    value={msg}
                    onChange={(e) => setMsg(e.target.value)}
                  />
                  <p className="text-xs text-[var(--fg-muted)]">{t("assistant.requestHint")}</p>
                  <div className="flex items-center gap-2 text-xs text-[var(--fg-muted)]">
                    <Switch
                      checked={verbatim}
                      onCheckedChange={setVerbatim}
                      label={t("assistant.verbatim")}
                    />
                    <span>{t("assistant.verbatim")}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      aria-label={t("assistant.channel")}
                      className="h-9 rounded-[var(--radius-sm)] border border-[var(--line-strong)] bg-transparent px-2 text-sm"
                      value={channel}
                      onChange={(e) => setChannel(e.target.value)}
                    >
                      <option value="">{t("assistant.autoChannel")}</option>
                      {c.channels.map((ch) => (
                        <option key={ch} value={ch}>
                          {t(`assistant.ch.${ch}` as "assistant.ch.app")}
                        </option>
                      ))}
                    </select>
                    <Button
                      loading={send.isPending}
                      disabled={!msg.trim()}
                      onClick={() => send.mutate()}
                    >
                      {t("assistant.send")}
                    </Button>
                  </div>
                  {send.error && (
                    <p role="alert" className="text-xs text-[var(--danger)]">
                      {send.error.message}
                    </p>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Calendar() {
  const t = useT();
  const qc = useQueryClient();
  const from = Date.now() - 3600_000;
  const q = useQuery({
    queryKey: ["calendar"],
    queryFn: () =>
      api<{
        timezone: string;
        events: Array<{ id: string; title: string; start_at: number; end_at: number }>;
        busy: Array<{ start: number; end: number; title: string }>;
        free: Array<{ start: number; end: number }>;
        feeds: Array<{ id: string; name: string; last_error: string | null }>;
      }>(`/assistant/calendar?from=${from}&to=${from + 14 * 86400000}`),
  });
  const shares = useQuery({
    queryKey: ["shares"],
    queryFn: () =>
      api<{
        sharedWith: Array<{ email: string; name: string }>;
        sharedWithMe: Array<{ email: string; name: string }>;
      }>("/assistant/calendar/shares"),
  });
  const [ev, setEv] = useState({ title: "", start: "", end: "" });
  const [feed, setFeed] = useState({ name: "", url: "" });
  const [email, setEmail] = useState("");
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["calendar"] });
    void qc.invalidateQueries({ queryKey: ["shares"] });
  };
  const addEv = useMutation({
    mutationFn: () =>
      api("/assistant/calendar/events", {
        method: "POST",
        body: {
          title: ev.title,
          start: new Date(ev.start).getTime(),
          end: new Date(ev.end).getTime(),
        },
      }),
    onSuccess: () => {
      setEv({ title: "", start: "", end: "" });
      refresh();
    },
  });
  const delEv = useMutation({
    mutationFn: (id: string) => api(`/assistant/calendar/events/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
  });
  const addFeed = useMutation({
    mutationFn: () => api("/assistant/calendar/feeds", { method: "POST", body: feed }),
    onSuccess: () => {
      setFeed({ name: "", url: "" });
      refresh();
    },
  });
  const delFeed = useMutation({
    mutationFn: (id: string) => api(`/assistant/calendar/feeds/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
  });
  const share = useMutation({
    mutationFn: (a: { email: string; on: boolean }) =>
      api("/assistant/calendar/shares", { method: "PUT", body: a }),
    onSuccess: () => {
      setEmail("");
      refresh();
    },
  });
  // Free/busy times are shown in the assistant's time zone (working hours are defined there), not the device's.
  const tz = q.data?.timezone;
  const fmt = (ms: number) =>
    new Date(ms).toLocaleString([], {
      timeZone: tz,
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  return (
    <div className="space-y-4">
      <Card className="space-y-2 p-3">
        <p className="text-sm font-medium">{t("assistant.addEvent")}</p>
        <Input
          dir="auto"
          aria-label={t("assistant.eventTitle")}
          placeholder={t("assistant.eventTitle")}
          value={ev.title}
          onChange={(e) => setEv({ ...ev, title: e.target.value })}
        />
        <div className="grid gap-2 sm:grid-cols-2">
          <Input
            type="datetime-local"
            aria-label={t("assistant.start")}
            value={ev.start}
            onChange={(e) => setEv({ ...ev, start: e.target.value })}
          />
          <Input
            type="datetime-local"
            aria-label={t("assistant.end")}
            value={ev.end}
            onChange={(e) => setEv({ ...ev, end: e.target.value })}
          />
        </div>
        <Button
          loading={addEv.isPending}
          disabled={!ev.title || !ev.start || !ev.end}
          onClick={() => addEv.mutate()}
        >
          {t("action.save")}
        </Button>
        {addEv.error && (
          <p role="alert" className="text-xs text-[var(--danger)]">
            {addEv.error.message}
          </p>
        )}
      </Card>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <h3 className="mb-2 text-sm font-semibold">{t("assistant.busy")}</h3>
          <ul className="space-y-1 text-sm">
            {(q.data?.busy ?? []).slice(0, 30).map((b) => (
              <li
                key={`${b.start}-${b.title}`}
                className="flex justify-between gap-2 rounded-lg border border-[var(--line)] px-2 py-1"
                dir="auto"
              >
                <span>{b.title}</span>
                <span className="text-xs text-[var(--fg-muted)]">{fmt(b.start)}</span>
                {q.data?.events.some((e) => e.start_at === b.start && e.title === b.title) && (
                  <button
                    type="button"
                    className="text-xs text-[var(--danger)]"
                    onClick={() =>
                      delEv.mutate(
                        q.data?.events.find((e) => e.start_at === b.start && e.title === b.title)
                          ?.id ?? "",
                      )
                    }
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
            {!q.data?.busy.length && (
              <li className="text-[var(--fg-muted)]">{t("assistant.nothing")}</li>
            )}
          </ul>
        </div>
        <div>
          <h3 className="mb-2 text-sm font-semibold">{t("assistant.free")}</h3>
          <ul className="space-y-1 text-sm">
            {(q.data?.free ?? []).slice(0, 12).map((s) => (
              <li
                key={s.start}
                className="rounded-lg border border-[var(--line)] px-2 py-1 text-[var(--success)]"
              >
                {fmt(s.start)} →{" "}
                {new Date(s.end).toLocaleTimeString([], {
                  timeZone: tz,
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Card className="space-y-2 p-3">
        <p className="text-sm font-medium">{t("assistant.feeds")}</p>
        <p className="text-xs text-[var(--fg-muted)]">{t("assistant.feedsHint")}</p>
        {q.data?.feeds.map((f) => (
          <div key={f.id} className="flex items-center justify-between text-sm">
            <span>
              {f.name}{" "}
              {f.last_error && (
                <span className="text-xs text-[var(--danger)]">({f.last_error})</span>
              )}
            </span>
            <Button variant="ghost" onClick={() => delFeed.mutate(f.id)}>
              {t("action.delete")}
            </Button>
          </div>
        ))}
        <div className="grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
          <Input
            aria-label={t("assistant.name")}
            placeholder={t("assistant.name")}
            value={feed.name}
            onChange={(e) => setFeed({ ...feed, name: e.target.value })}
          />
          <Input
            dir="ltr"
            autoComplete="off"
            aria-label="iCal URL"
            placeholder="https://calendar.google.com/…/basic.ics"
            value={feed.url}
            onChange={(e) => setFeed({ ...feed, url: e.target.value })}
          />
          <Button
            loading={addFeed.isPending}
            disabled={feed.url.length < 12}
            onClick={() => addFeed.mutate()}
          >
            {t("assistant.connect")}
          </Button>
        </div>
        {addFeed.error && (
          <p role="alert" className="text-xs text-[var(--danger)]">
            {addFeed.error.message}
          </p>
        )}
      </Card>
      <Card className="space-y-2 p-3">
        <p className="text-sm font-medium">{t("assistant.shares")}</p>
        <p className="text-xs text-[var(--fg-muted)]">{t("assistant.sharesHint")}</p>
        {shares.data?.sharedWith.map((s) => (
          <div key={s.email} className="flex items-center justify-between text-sm">
            <span dir="auto">
              {s.name}{" "}
              <span className="text-xs text-[var(--fg-muted)]" dir="ltr">
                {s.email}
              </span>
            </span>
            <Button variant="ghost" onClick={() => share.mutate({ email: s.email, on: false })}>
              {t("assistant.stopShare")}
            </Button>
          </div>
        ))}
        {!!shares.data?.sharedWithMe.length && (
          <p className="text-xs text-[var(--fg-muted)]">
            {t("assistant.sharedWithMe", {
              names: shares.data.sharedWithMe.map((s) => s.name).join(", "),
            })}
          </p>
        )}
        <div className="flex gap-2">
          <Input
            dir="ltr"
            aria-label="Email"
            placeholder="friend@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button
            loading={share.isPending}
            disabled={!email.includes("@")}
            onClick={() => share.mutate({ email, on: true })}
          >
            {t("assistant.share")}
          </Button>
        </div>
        {share.error && (
          <p role="alert" className="text-xs text-[var(--danger)]">
            {share.error.message}
          </p>
        )}
      </Card>
    </div>
  );
}

function TelegramCard() {
  const t = useT();
  const qc = useQueryClient();
  const st = useQuery({
    queryKey: ["telegram"],
    queryFn: () =>
      api<{
        connected: boolean;
        bot: string | null;
        linked: boolean;
        forward: boolean;
        briefingHour: number | null;
      }>("/assistant/telegram"),
    refetchInterval: (q) => (q.state.data?.connected && !q.state.data.linked ? 3000 : false),
  });
  const [token, setToken] = useState("");
  const [link, setLink] = useState<{ code: string; url: string | null } | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["telegram"] });
  const connect = useMutation({
    mutationFn: () => api("/assistant/telegram", { method: "PUT", body: { token } }),
    onSuccess: () => {
      setToken("");
      refresh();
    },
  });
  const makeLink = useMutation({
    mutationFn: () =>
      api<{ code: string; url: string | null }>("/assistant/telegram/link", {
        method: "POST",
        body: {},
      }),
    onSuccess: (r) => setLink(r),
  });
  const settings = useMutation({
    mutationFn: (b: { forward?: boolean; briefingHour?: number | null }) =>
      api("/assistant/telegram/settings", { method: "PUT", body: b }),
    onSuccess: refresh,
  });
  const off = useMutation({
    mutationFn: () => api("/assistant/telegram", { method: "DELETE" }),
    onSuccess: () => {
      setLink(null);
      refresh();
    },
  });
  const d = st.data;
  return (
    <Card className="space-y-3 p-3">
      <p className="text-sm font-medium">{t("telegram.title")}</p>
      <p className="text-xs text-[var(--fg-muted)]">{t("telegram.hint")}</p>
      {!d?.connected && (
        <>
          <ol className="list-decimal space-y-1 ps-5 text-xs text-[var(--fg-muted)]">
            <li>{t("telegram.step1")}</li>
            <li>{t("telegram.step2")}</li>
          </ol>
          <div className="flex gap-2">
            <Input
              dir="ltr"
              type="password"
              autoComplete="off"
              placeholder="123456789:AA…"
              aria-label={t("telegram.token")}
              value={token}
              onChange={(e) => setToken(e.target.value)}
            />
            <Button
              loading={connect.isPending}
              disabled={token.trim().length < 20}
              onClick={() => connect.mutate()}
            >
              {t("telegram.connect")}
            </Button>
          </div>
          {connect.error && (
            <p role="alert" className="text-xs text-[var(--danger)]">
              {connect.error.message}
            </p>
          )}
        </>
      )}
      {d?.connected && (
        <div className="space-y-3 text-sm">
          <p>
            🤖 @{d.bot} · {d.linked ? t("telegram.linked") : t("telegram.notLinked")}
          </p>
          {!d.linked && (
            <div className="space-y-2">
              <Button loading={makeLink.isPending} onClick={() => makeLink.mutate()}>
                {t("telegram.getLink")}
              </Button>
              {link && (
                <p className="text-xs text-[var(--fg-muted)]">
                  {link.url ? (
                    <a className="underline" href={link.url} target="_blank" rel="noreferrer">
                      {t("telegram.open")}
                    </a>
                  ) : null}{" "}
                  · {t("telegram.orSend")} <code dir="ltr">/start {link.code}</code>
                </p>
              )}
            </div>
          )}
          {d.linked && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Switch
                  checked={d.forward}
                  onCheckedChange={(v) => settings.mutate({ forward: v })}
                  label={t("telegram.forward")}
                />
                <span>{t("telegram.forward")}</span>
              </div>
              <label className="flex items-center gap-2">
                {t("telegram.briefing")}
                <select
                  className="rounded-lg border border-[var(--line)] bg-[var(--surface)] px-2 py-1"
                  value={d.briefingHour ?? ""}
                  onChange={(e) =>
                    settings.mutate({
                      briefingHour: e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                >
                  <option value="">{t("telegram.off")}</option>
                  {Array.from({ length: 24 }, (_, h) => h).map((h) => (
                    <option key={h} value={h}>
                      {String(h).padStart(2, "0")}:00
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <Button variant="outline" onClick={() => off.mutate()}>
            {t("telegram.disconnect")}
          </Button>
        </div>
      )}
    </Card>
  );
}

function Channels() {
  const t = useT();
  const messaging = useFeature("messaging");
  const qc = useQueryClient();
  const st = useQuery({
    queryKey: ["channels"],
    queryFn: () => api<{ twilio: boolean; email: boolean; origin: string }>("/assistant/channels"),
  });
  const hooks = useQuery({
    queryKey: ["prefs"],
    queryFn: () =>
      api<{ webhooks: { sms: string; voice: string; email: string } }>("/assistant/prefs"),
  });
  const prefs = useQuery({
    queryKey: ["assistant-prefs"],
    queryFn: () =>
      api<{
        prefs: { voiceGender: "female" | "male"; quietFrom: number; quietTo: number };
      }>("/assistant/prefs"),
  });
  const savePrefs = useMutation({
    mutationFn: (b: { voiceGender?: string; quietFrom?: number; quietTo?: number }) =>
      api("/assistant/prefs", { method: "PUT", body: b }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["assistant-prefs"] }),
  });
  const [tw, setTw] = useState({ sid: "", token: "", from: "", whatsappFrom: "" });
  const [em, setEm] = useState({ apiKey: "", from: "" });
  const done = () => void qc.invalidateQueries({ queryKey: ["channels"] });
  const saveTw = useMutation({
    mutationFn: () =>
      api("/assistant/channels/twilio", {
        method: "PUT",
        body: { ...tw, whatsappFrom: tw.whatsappFrom || undefined },
      }),
    onSuccess: () => {
      setTw({ sid: "", token: "", from: "", whatsappFrom: "" });
      done();
    },
  });
  const saveEm = useMutation({
    mutationFn: () => api("/assistant/channels/email", { method: "PUT", body: em }),
    onSuccess: () => {
      setEm({ apiKey: "", from: "" });
      done();
    },
  });
  const clear = useMutation({
    mutationFn: (w: string) => api(`/assistant/channels/${w}`, { method: "DELETE" }),
    onSuccess: done,
  });
  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--fg-muted)]">{t("assistant.channelsHint")}</p>
      {messaging && <TelegramCard />}
      {prefs.data && (
        <Card className="space-y-3 p-3">
          <p className="text-sm font-medium">{t("assistant.voicePrefs")}</p>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <label className="flex items-center gap-2">
              {t("assistant.voiceGender")}
              <select
                className="rounded-lg border border-[var(--line)] bg-[var(--surface)] px-2 py-1"
                value={prefs.data.prefs.voiceGender}
                onChange={(e) => savePrefs.mutate({ voiceGender: e.target.value })}
              >
                <option value="female">{t("assistant.female")}</option>
                <option value="male">{t("assistant.male")}</option>
              </select>
            </label>
            <label className="flex items-center gap-2">
              {t("assistant.quietFrom")}
              <select
                className="rounded-lg border border-[var(--line)] bg-[var(--surface)] px-2 py-1"
                value={prefs.data.prefs.quietFrom}
                onChange={(e) => savePrefs.mutate({ quietFrom: Number(e.target.value) })}
              >
                {Array.from({ length: 24 }, (_, h) => h).map((h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, "0")}:00
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2">
              {t("assistant.quietTo")}
              <select
                className="rounded-lg border border-[var(--line)] bg-[var(--surface)] px-2 py-1"
                value={prefs.data.prefs.quietTo}
                onChange={(e) => savePrefs.mutate({ quietTo: Number(e.target.value) })}
              >
                {Array.from({ length: 24 }, (_, h) => h).map((h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, "0")}:00
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="text-xs text-[var(--fg-muted)]">{t("assistant.quietHint")}</p>
        </Card>
      )}
      <Card className="space-y-2 p-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">Twilio — SMS / WhatsApp / {t("assistant.calls")}</p>
          <span
            className={`text-xs ${st.data?.twilio ? "text-[var(--success)]" : "text-[var(--fg-muted)]"}`}
          >
            {st.data?.twilio ? t("assistant.connected") : t("signin.notConfigured")}
          </span>
        </div>
        <Input
          dir="ltr"
          aria-label="Account SID"
          placeholder="ACxxxxxxxx…"
          value={tw.sid}
          onChange={(e) => setTw({ ...tw, sid: e.target.value })}
        />
        <Input
          dir="ltr"
          type="password"
          autoComplete="off"
          aria-label="Auth token"
          placeholder="Auth token"
          value={tw.token}
          onChange={(e) => setTw({ ...tw, token: e.target.value })}
        />
        <div className="grid gap-2 sm:grid-cols-2">
          <Input
            dir="ltr"
            aria-label="From number"
            placeholder="+1500… (SMS / calls)"
            value={tw.from}
            onChange={(e) => setTw({ ...tw, from: e.target.value })}
          />
          <Input
            dir="ltr"
            aria-label="WhatsApp number"
            placeholder="+1415… (WhatsApp, optional)"
            value={tw.whatsappFrom}
            onChange={(e) => setTw({ ...tw, whatsappFrom: e.target.value })}
          />
        </div>
        <div className="flex gap-2">
          <Button
            loading={saveTw.isPending}
            disabled={!tw.sid || !tw.token || !tw.from}
            onClick={() => saveTw.mutate()}
          >
            {t("action.save")}
          </Button>
          {st.data?.twilio && (
            <Button variant="outline" onClick={() => clear.mutate("twilio")}>
              {t("action.delete")}
            </Button>
          )}
        </div>
        {saveTw.error && (
          <p role="alert" className="text-xs text-[var(--danger)]">
            {saveTw.error.message}
          </p>
        )}
        <p className="text-xs text-[var(--fg-muted)]">{t("assistant.twilioHook")}</p>
        {hooks.data && (
          <div className="space-y-1">
            <Input
              readOnly
              dir="ltr"
              aria-label="SMS webhook"
              value={hooks.data.webhooks.sms}
              onFocus={(e) => e.currentTarget.select()}
            />
            <Input
              readOnly
              dir="ltr"
              aria-label="Voice webhook"
              value={hooks.data.webhooks.voice}
              onFocus={(e) => e.currentTarget.select()}
            />
          </div>
        )}
      </Card>
      <Card className="space-y-2 p-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">{t("assistant.email")} (Resend)</p>
          <span
            className={`text-xs ${st.data?.email ? "text-[var(--success)]" : "text-[var(--fg-muted)]"}`}
          >
            {st.data?.email ? t("assistant.connected") : t("signin.notConfigured")}
          </span>
        </div>
        <Input
          dir="ltr"
          type="password"
          autoComplete="off"
          aria-label="Resend API key"
          placeholder="re_…"
          value={em.apiKey}
          onChange={(e) => setEm({ ...em, apiKey: e.target.value })}
        />
        <Input
          dir="ltr"
          aria-label="From"
          placeholder="My assistant <assistant@yourdomain.com>"
          value={em.from}
          onChange={(e) => setEm({ ...em, from: e.target.value })}
        />
        <div className="flex gap-2">
          <Button
            loading={saveEm.isPending}
            disabled={!em.apiKey || !em.from}
            onClick={() => saveEm.mutate()}
          >
            {t("action.save")}
          </Button>
          {st.data?.email && (
            <Button variant="outline" onClick={() => clear.mutate("email")}>
              {t("action.delete")}
            </Button>
          )}
        </div>
        {saveEm.error && (
          <p role="alert" className="text-xs text-[var(--danger)]">
            {saveEm.error.message}
          </p>
        )}
        <p className="text-xs text-[var(--fg-muted)]">{t("assistant.emailHint")}</p>
      </Card>
    </div>
  );
}

const TABS: Array<[Tab, typeof MessageSquare, "assistant.tab.threads"]> = [
  ["errands", ListChecks, "assistant.tab.errands" as never],
  ["threads", MessageSquare, "assistant.tab.threads"],
  ["contacts", ContactIcon, "assistant.tab.contacts" as never],
  ["calendar", CalendarDays, "assistant.tab.calendar" as never],
  ["channels", Plug, "assistant.tab.channels" as never],
];

/** Personal assistant workspace: what it is saying for you, who it can reach, when you are free, how it connects. */
export function AssistantPage() {
  const t = useT();
  const { sidebarOpen, set } = usePrefs();
  const [tab, setTab] = useState<Tab>("errands");
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-[var(--line)] px-3">
        <button
          type="button"
          onClick={() => set({ sidebarOpen: !sidebarOpen })}
          aria-label={t("sidebar.toggle")}
          className={`grid size-9 place-items-center rounded-lg hover:bg-[var(--surface-3)] ${sidebarOpen ? "lg:hidden" : ""}`}
        >
          <PanelLeft className="size-5 rtl:-scale-x-100" />
        </button>
        <h1 className="font-display text-lg font-semibold">{t("nav.assistant")}</h1>
      </header>
      <div
        role="tablist"
        className="flex gap-1 overflow-x-auto border-b border-[var(--line)] px-3 py-2"
      >
        {TABS.map(([k, Icon, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm ${tab === k ? "bg-[var(--surface-3)] text-[var(--fg)]" : "text-[var(--fg-muted)] hover:bg-[var(--surface-3)]"}`}
          >
            <Icon className="size-4" /> {t(label)}
          </button>
        ))}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-3xl">
          {tab === "errands" && <Errands />}
          {tab === "threads" && <Threads />}
          {tab === "contacts" && <Contacts />}
          {tab === "calendar" && <Calendar />}
          {tab === "channels" && <Channels />}
        </div>
      </div>
    </div>
  );
}
